
import { useState, useEffect, useCallback } from "react";
import { useSiteSettings, useSiteSettingsMutation } from "@/hooks/useSiteSettings";

interface SiteSettings {
  id?: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  site_title: string;
  site_description: string;
  social_facebook?: string;
  social_instagram?: string;
  social_youtube?: string;
  social_tiktok?: string;
  privacy_policy_content?: string;
  terms_of_service_content?: string;
  privacy_effective_date?: string;
  terms_effective_date?: string;
  copyright_start_year?: number;
  legal_email?: string;
  legal_phone?: string;
  legal_address?: string;
}

const DEFAULT_SETTINGS: SiteSettings = {
  contact_email: "contact@boboandteedee.com",
  contact_phone: "+1 (345) 923-5116",
  contact_address: "Grand Cayman, Cayman Islands",
  site_title: "Bobo & Teedee®",
  site_description: "Educational entertainment for children featuring Cayman Islands culture",
  privacy_policy_content: "",
  terms_of_service_content: "",
  privacy_effective_date: new Date().toISOString().split('T')[0],
  terms_effective_date: new Date().toISOString().split('T')[0],
  copyright_start_year: new Date().getFullYear(),
  legal_email: "legal@boboteedee.ky",
  legal_phone: "+1 (345) 123-4567",
  legal_address: "Bobo & Teedee® Legal Department, Grand Cayman, Cayman Islands"
};

export const useAdminSiteSettings = () => {
  const { data: fetchedSettings, isLoading, error: fetchError, dataUpdatedAt } = useSiteSettings();
  const mutation = useSiteSettingsMutation();
  
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);

  // Update local state whenever fetched settings change
  useEffect(() => {
    if (fetchedSettings) {
      console.log("Updating local settings with fetched data:", fetchedSettings);
      console.log("Data updated at:", new Date(dataUpdatedAt));
      setSettings(fetchedSettings);
    }
  }, [fetchedSettings, dataUpdatedAt]);

  const handleSave = useCallback(async () => {
    console.log("Admin handleSave called with current settings:", settings);
    console.log("Attempting to save site settings...");
    
    try {
      await mutation.mutateAsync(settings);
      console.log("Settings saved successfully via mutation!");
    } catch (error) {
      console.error("Error saving settings:", error);
      // The mutation will handle showing the toast error
    }
  }, [settings, mutation]);

  const handleInputChange = useCallback((field: keyof SiteSettings, value: string | number) => {
    console.log(`Updating field ${field} to:`, value);
    setSettings(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    settings,
    loading: isLoading,
    saving: mutation.isPending,
    error: fetchError || mutation.error,
    handleSave,
    handleInputChange,
  };
};
